import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import Paginated from "../../components/Paginated";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { Palette, UserCheck, Truck, Download, AlertTriangle } from "lucide-react";
import ZoomImage from "../../components/ZoomImage";

// Download a reference image with a descriptive filename so the karigar/carrier
// knows exactly which job, weight and purity it belongs to.
async function downloadImage(url, name) {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch { window.open(url, "_blank"); }
}

const STATUSES = ["new", "assigned", "in_progress", "ready", "delivered", "cancelled"];

/**
 * Customisation Requests.
 *
 * Super Admin / Major Admin see every request. A salesperson or admin sees
 * only the ones raised while they were assisting the customer — enforced by
 * the backend, not just hidden here.
 */
export default function CustomisationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [pending, setPending] = useState([]);
  const canAssignView = hasRole(user, ["super_admin", "major_admin", "admin"]);
  const isBoss = hasRole(user, ["super_admin", "major_admin"]);

  const load = () => {
    const q = status ? `?status=${status}` : "";
    api.get(`/api/crm/customisations${q}`).then((r) => setItems(r.data || [])).catch(() => {});
    if (canAssignView) api.get("/api/crm/customisations/pending").then((r) => setPending(r.data?.pending || [])).catch(() => {});
  };
  useEffect(() => { load(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]);
  useEffect(() => { api.get("/api/crm/karigars").then((r) => setKarigars(r.data || [])).catch(() => {}); }, []);

  const assign = async (req, karigarId) => {
    try {
      const { data } = await api.post(`/api/crm/customisations/${req.id}/assign`, { karigar_id: karigarId || "" });
      toast.success(`Assigned to ${data.karigar_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  const setField = async (req, patch) => {
    try {
      await api.post(`/api/crm/customisations/${req.id}/status`, patch);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Made to order</div>
        <h1 className="font-display text-4xl text-maroon">Customisation Requests</h1>
        <p className="text-xs text-ash mt-1">
          {isBoss ? "Every customisation across the business." : "Customisations you raised while assisting customers."}
        </p>
      </div>

      {/* Pending to assign — needs a karigar */}
      {canAssignView && pending.length > 0 && (
        <div className="bg-gold/10 border border-gold rounded-lg p-4 mb-6" data-testid="pending-assign-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-maroon"/>
            <div className="font-medium text-maroon">{pending.length} customisation order{pending.length === 1 ? "" : "s"} pending assignment</div>
          </div>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 bg-white border border-line p-2.5 rounded" data-testid={`pending-${r.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-medium">{r.item_name} <span className="text-[10px] text-ash">· {r.customer_name || r.customer_phone}</span></div>
                  <div className="text-[10px] text-ash">
                    Raised by {r.raised_by_name || "—"} ({(r.raised_by_role || "").replace("_", " ")}) · {r.age_days}d ago
                    {(r.flags || []).includes("not_assigned") && <span className="ml-1 text-red-600 font-medium uppercase tracking-widest text-[9px]">Not assigned</span>}
                  </div>
                </div>
                <select className="text-xs border border-line px-2 py-1 bg-pearl" defaultValue="" onChange={(e) => e.target.value && assign(r, e.target.value === "auto" ? "" : e.target.value)} data-testid={`pending-assign-${r.id}`}>
                  <option value="">Assign karigar…</option>
                  <option value="auto">Auto (least busy)</option>
                  {karigars.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setStatus("")} className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${!status ? "bg-maroon text-pearl border-maroon" : "border-line text-ash"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${status === s ? "bg-maroon text-pearl border-maroon" : "border-line text-ash hover:border-maroon"}`}
            data-testid={`cust-filter-${s}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <Paginated items={items} pageSize={15} label="requests" scroller>
        {(pageRows) => (
      <div className="bg-white border border-line">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Order", "Customer", "Item", "Payment", "Total", "Paid (80%)", "Pending", "Balance", "Raised By", "Karigar", "Status", ""].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-b border-line/60" data-testid={`cust-${r.id}`}>
                  <td className="p-3 font-mono text-xs">{(r.order_id || "").slice(0, 8).toUpperCase()}</td>
                  <td className="p-3">
                    <div>{r.customer_name || "—"}</div>
                    <div className="text-[10px] text-ash">{r.customer_phone}</div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Palette size={12} className="text-gold"/>
                      <span>{r.item_name}</span>
                    </div>
                    <div className="text-[10px] text-ash">{r.weight_grams}g · {r.purity}</div>
                  </td>
                  <td className="p-3 text-xs">
                    <div>{r.payment_type || "online"}</div>
                    <div className="text-[10px] text-ash font-mono">{(r.payment_id || "").slice(0, 14) || "—"}</div>
                  </td>
                  <td className="p-3">{fmtINR(r.total_amount || 0)}</td>
                  <td className="p-3 text-green-700">{fmtINR(r.advance_paid || 0)}</td>
                  <td className="p-3 text-maroon">{fmtINR(r.pending_amount || 0)}</td>
                  <td className="p-3">
                    <select
                      className={`text-[11px] border px-1 py-0.5 ${r.balance_status === "paid" ? "border-green-600 text-green-700" : "border-line text-ash"}`}
                      value={r.balance_status || "pending"}
                      onChange={(e) => setField(r, { balance_status: e.target.value })}
                      data-testid={`cust-balance-${r.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                    </select>
                  </td>
                  <td className="p-3 text-xs">
                    {r.raised_by_name || <span className="text-ash">Online</span>}
                    {r.raised_by_role && <div className="text-[10px] text-ash">{r.raised_by_role.replace("_", " ")}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {r.karigar_name ? (
                      <>
                        <span className="inline-flex items-center gap-1 text-maroon"><UserCheck size={12}/>{r.karigar_name}</span>
                        {r.assigned_by_name && <div className="text-[9px] text-ash">by {r.assigned_by_name} ({(r.assigned_by_role || "").replace("_", " ")}){r.assigned_at ? ` · ${new Date(r.assigned_at).toLocaleDateString()}` : ""}</div>}
                      </>
                    ) : (
                      <div className="flex items-center gap-1">
                        <select
                          className="text-[11px] border border-line px-1 py-0.5 max-w-[9rem]"
                          defaultValue=""
                          onChange={(e) => assign(r, e.target.value)}
                          data-testid={`cust-assign-${r.id}`}
                        >
                          <option value="" disabled>Assign…</option>
                          <option value="">Auto (least busy)</option>
                          {karigars.map((k) => <option key={k.id} value={k.id}>{k.name} ({k.open_jobs})</option>)}
                        </select>
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <select
                      className="text-[11px] border border-line px-1 py-0.5"
                      value={r.status}
                      onChange={(e) => setField(r, { status: e.target.value })}
                      data-testid={`cust-status-${r.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="text-xs text-maroon underline whitespace-nowrap" data-testid={`cust-expand-${r.id}`}>
                      {expanded === r.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>

                {expanded === r.id && (
                  <tr className="bg-pearl/40">
                    <td colSpan={12} className="p-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="eyebrow text-[10px] mb-1">What the customer asked for</div>
                          <p className="text-sm text-ink">{r.customisation_note || <span className="text-ash">No text supplied</span>}</p>
                          {(r.customisation_images || []).length > 0 && (
                            <div className="flex flex-wrap gap-3 mt-3">
                              {r.customisation_images.map((u, i) => {
                                const fname = [
                                  "CUST", (r.id || "").slice(0, 8).toUpperCase(),
                                  r.weight_grams ? `${r.weight_grams}g` : "",
                                  r.purity || "", r.metal || "",
                                  (r.customer_name || "").replace(/\s+/g, "-"),
                                  `${i + 1}`,
                                ].filter(Boolean).join("_") + ".jpg";
                                return (
                                  <div key={i} className="text-center">
                                    <div className="w-28 h-28 border border-line overflow-hidden hover:border-maroon">
                                      <ZoomImage src={u} alt={`Reference ${i + 1}`} className="w-full h-full" zoom={3} />
                                    </div>
                                    <button
                                      onClick={() => downloadImage(u, fname)}
                                      className="text-[10px] text-maroon underline inline-flex items-center gap-1 mt-1"
                                      title={fname}
                                      data-testid={`ref-download-${r.id}-${i}`}
                                    >
                                      <Download size={11}/> Download
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="eyebrow text-[10px] mb-1">Delivery</div>
                          <div className="text-sm">
                            {r.delivery_mode === "pickup" ? "Store pickup" : "Home delivery"}
                          </div>
                          {r.delivery_mode !== "pickup" && (
                            <>
                              <div className="text-[11px] text-ash mt-1">
                                {[r.address?.line1, r.address?.city, r.address?.state, r.address?.pincode].filter(Boolean).join(", ") || "No address on the order"}
                              </div>
                              <div className="mt-3">
                                <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">
                                  <Truck size={11} className="inline mr-1"/>Sent through
                                </label>
                                <input
                                  defaultValue={r.delivery_through || ""}
                                  onBlur={(e) => e.target.value !== (r.delivery_through || "") && setField(r, { delivery_through: e.target.value })}
                                  placeholder="Courier / person carrying it"
                                  className="border border-line px-2 py-1 text-sm bg-white w-full max-w-xs"
                                  data-testid={`cust-delivery-${r.id}`}
                                />
                              </div>
                            </>
                          )}

                          <div className="eyebrow text-[10px] mt-4 mb-1">History</div>
                          <ul className="text-[11px] text-ash space-y-0.5">
                            {(r.status_history || []).map((h, i) => (
                              <li key={i}>
                                {h.at ? new Date(h.at).toLocaleString() : ""} — {h.status || h.balance_status || "updated"}
                                {h.by ? ` by ${h.by}` : ""}{h.note ? ` (${h.note})` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="p-12 text-center text-ash text-sm">
            <Palette size={20} className="mx-auto mb-2 opacity-40"/>
            No customisation requests yet.
          </div>
        )}
      </div>
        )}
      </Paginated>
    </div>
  );
}
